
import Page from '@root/common/base/Page';
import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import PodcastSkeletonLoader from '@root/common/components/base/loader/PodcastSkeletonLoader.vue';
import PodcastCategoryService from '@root/modules/podcast/services/podcastCategory.service';
import PodcastEpisodeService from '@root/modules/podcast/services/podcastEpisode.service';
import PodcastsCategory from '@root/modules/podcast/components/PodcastsCategory.vue';
import PodcastsCategoryRadio from '@root/modules/podcast/components/PodcastsCategoryRadio.vue';
import PodcastDetail from '@root/modules/podcast/components/PodcastDetail.vue';
import PodcastPaywall from '@root/modules/podcast/components/PodcastPaywall.vue';
import { PodcastCategory } from '@root/modules/podcast/types/podcast';
import buildPictureUrl from '@core/utils/buildPictureUrl';
import getMediaData from '@root/modules/podcast/utils/getMediaData';
import { limit } from '@root/modules/podcast/config/podcast.config';
import getCategoryAdsSettings from '@root/modules/category/utils/getCategoryAdsSettings';
import isMobileView from '@root/common/utils/isMobileView';
import { radioStreams } from '@root/modules/podcast/config/podcast.config';
import buildPodcastUrl from '@root/modules/podcast/utils/buildPodcastUrl';
import buildCategoryUrl from '@root/modules/category/utils/buildCategoryUrl';

interface Data {
  category: PodcastCategory | null;
  component: string | null;
  isMobileView: boolean;
  loaded: boolean;
  offset: number;
  categoriesOffset: number;
  limit: number;
  shouldLoadMoreCategories: boolean;
  shoudLoadMoreEpisodes: boolean;
  loadingCategories: boolean;
  loadingEpisodes: boolean;
  key: number | null;
}

interface Computed {
  showPaywall: boolean;
  showPlayer: boolean;
}

interface Methods {
  loadMoreCategories: () => void;
  loadMoreEpisodes: () => void;
}

export default Page.extend<Data, Methods, Computed, unknown>({
  components: {
    AdsLayout,
    PodcastSkeletonLoader,
    PodcastsCategory,
    PodcastsCategoryRadio,
    PodcastDetail,
    PodcastPaywall,
  },
  data() {
    return {
      category: null,
      component: null,
      isMobileView: false,
      loaded: false,
      offset: 0,
      categoriesOffset: 0,
      limit,
      shouldLoadMoreCategories: true,
      shoudLoadMoreEpisodes: true,
      loadingCategories: false,
      loadingEpisodes: false,
      key: null,
    };
  },
  computed: {
    showPaywall() {
      return this.$store.state.podcast.showPaywall;
    },
    showPlayer() {
      return Boolean(this.$player?.state.currentlyPlaying.audioId);
    },
  },
  methods: {
    async loadMoreCategories() {
      if (!this.category || this.loadingCategories) {
        return;
      }

      this.loadingCategories = true;

      const { params, query } = this.$route;
      const variables = {
        parent: params.parent,
        slug: [params.slug],
        page: query.page as string,
        offset: this.categoriesOffset + this.limit,
        getArticles: false,
      };

      const podcastCategoryService = new PodcastCategoryService();
      const category = await podcastCategoryService.fetch(variables);
      if (category?.categories.items.length) {
        this.category.categories.items = [...this.category?.categories.items, ...category?.categories.items];
        this.key = this.category.categories.items.length;
        this.categoriesOffset = this.categoriesOffset + this.limit;
      } else {
        this.shouldLoadMoreCategories = false;
      }

      this.loadingCategories = false;
    },
    async loadMoreEpisodes() {
      if (!this.category || this.loadingEpisodes) {
        return;
      }

      this.loadingEpisodes = true;

      const podcastEpisodeService = new PodcastEpisodeService();
      const categoryId = [this.category?.id];
      const orderDirection = this.category.parentCategory?.parentCategory?.id === 96603927 ? 'ASC' : 'DESC';
      const episodes = await podcastEpisodeService.fetch({ categoryId, offset: this.offset + this.limit, limit: this.limit, orderDirection });

      if (episodes?.length) {
        this.category.articles.items = [...this.category?.articles.items, ...episodes];
        this.key = this.category.articles.items.length;
        this.offset = this.offset + this.limit;
      } else {
        this.shoudLoadMoreEpisodes = false;
      }

      this.loadingEpisodes = false;
    },
  },
  beforeRouteLeave(to, from, next) {
    this.resetPageAdsSettings();

    next();
  },
  mounted() {
    this.restoreScroll();
    this.isMobileView = isMobileView();
  },
  async fetch() {
    try {
      const { params, query } = this.$route;
      const variables = {
        parent: params.parent,
        slug: [params.slug],
        page: query.page as string,
        getArticles: false,
      };

      const podcastCategoryService = new PodcastCategoryService();
      const category = await podcastCategoryService.fetch(variables);
      if (category) {
        this.component = category.id === 96604123 ? 'PodcastsCategoryRadio' : category.categories.items.length ? 'PodcastsCategory' : 'PodcastDetail';
        this.category = category;
        this.key = this.category.categories.items.length;

        // Reload category with articles
        if (this.component === 'PodcastDetail') {
          const orderDirection = category.parentCategory?.parentCategory?.id === 96603927 ? 'ASC' : 'DESC';
          const categoryWithArticles = await podcastCategoryService.fetch({ ...variables, getArticles: true, orderDirection, limit });
          if (categoryWithArticles) {
            // Get audio duration
            const mediaData = await getMediaData(categoryWithArticles.articles.items, null, false);
            if (mediaData?.length) {
              const categoryItemsWithAudioData = categoryWithArticles.articles.items.map((item) => {
                const mediaAsset = mediaData.find((mediaDataItem) => mediaDataItem.id === item.id);
                return { ...item, duration: mediaAsset?.assetDuration || null, video: mediaAsset?.video };
              });

              categoryWithArticles.articles.items = categoryItemsWithAudioData;
            }

            this.category = categoryWithArticles;
            this.key = this.category.articles.items.length;
          }
        }

        // Reload categoy with articles containing radio stream data
        if (this.component === 'PodcastsCategoryRadio') {
          const categoryWithArticles = await podcastCategoryService.fetch({ ...variables, getArticles: true });
          if (categoryWithArticles) {
            const categoryItemsWithStreamData = categoryWithArticles.articles.items.map((item) => {
              const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
              const href = buildPodcastUrl({ article: item, domain: 'www.delfi.lt/fone', fakeDomainEnabled });
              const streamUrl = radioStreams.find((stream) => stream.id === item.id)?.url;
              return { ...item, href, streamUrl };
            });

            categoryWithArticles.articles.items = categoryItemsWithStreamData;

            this.category = categoryWithArticles;
          }
        }

        this.setPageAdsSettings(getCategoryAdsSettings({ $channelConfig: this.$channelConfig, route: this.$route, category }));

        this.loaded = true;
      } else {
        // 404?
      }
    } catch (e) {
      this.handlePageError(e);
    }
  },
  head() {
    const settings = this.$channelConfig('meta').base;
    const meta = this.getPageMeta();
    const cXenseMeta = [];
    const script = [];

    const pageTitle = this.category?.attributes?.title || this.category?.name || settings.title;
    meta.setTitle({ title: pageTitle });

    if (this.category?.attributes?.description) {
      meta.description = this.category?.attributes.description;
      meta.ogDescription = this.category?.attributes.description;
    }

    if (this.category?.attributes?.keywords) {
      if (Array.isArray(this.category?.attributes.keywords)) {
        meta.keywords = this.category?.attributes.keywords.join();
      } else {
        meta.keywords = this.category?.attributes.keywords;
      }
    }

    if (this.category?.attributes?.image) {
      meta.imageData = {
        width: 1200,
        height: 800,
        src: buildPictureUrl({ id: this.category?.attributes.image.id, cropperData: this.category?.attributes.image.attributes.cropperData }),
      };
    }

    cXenseMeta.push({ hid: 'cXenseParse:eks-article-section-id', name: 'cXenseParse:eks-article-section-id', content: String(this.category?.id) });

    if (this.component === 'PodcastsCategory' && this.category?.parentCategory?.id !== 96603972) {
      // Inject ld+json into head
      const jsonLd: any = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Delfi tinklalaidžių sąrašas',
        itemListElement: [],
      };

      const domain = 'www.delfi.lt';
      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;

      this.category?.categories.items.forEach((category) => {
        jsonLd.itemListElement.push({ '@type': 'PodcastSeries', name: category.name, url: buildCategoryUrl({ category, domain, fakeDomainEnabled }) });
      });

      script.push({ hid: 'category-ld-json', type: 'application/ld+json', json: jsonLd });
    }

    return {
      title: meta.title,
      meta: [...meta.data, ...cXenseMeta],
      link: meta.link,
      script,
    };
  },
});
